import React, { useState } from 'react';
import { ArrowLeftIcon, FileTextIcon, TruckIcon, CalendarIcon, DollarSignIcon, BuildingIcon, ClipboardListIcon, CheckCircleIcon, XCircleIcon, FileIcon, DownloadIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
interface Document {
  type: string;
  fileName: string;
}
interface Request {
  id: string;
  truckingCompany: string;
  jobNumber: string;
  jobName: string;
  completionDate: string;
  requestDate: string;
  amount: number;
  invoiceNumber: string;
  status: string;
  priority: string;
  contractor: string;
  contractorRating: string;
  completedLoads: number;
  totalHours: number;
  documents: Document[];
  notes: string;
  paymentTerms: string;
  factorRate: number;
  approvalDate?: string;
  achFileName?: string;
  achGenerationDate?: string;
  expectedPaymentDate?: string;
}
interface RequestDetailsProps {
  request: Request;
  onClose: () => void;
}
export const RequestDetails: React.FC<RequestDetailsProps> = ({
  request,
  onClose
}) => {
  const [decisionNotes, setDecisionNotes] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showDenyConfirm, setShowDenyConfirm] = useState(false);
  const [customFactorRate, setCustomFactorRate] = useState(request?.factorRate || 2.5);
  if (!request) {
    return <div className="bg-white rounded-lg shadow p-6" data-id="element-3145">
        <div className="flex items-center justify-between mb-4" data-id="element-3146">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3147">
            <ArrowLeftIcon size={18} data-id="element-3148" />
            Back
          </button>
        </div>
        <div className="text-center py-12" data-id="element-3149">
          <AlertTriangleIcon size={48} className="mx-auto text-yellow-500 mb-4" data-id="element-3150" />
          <h2 className="text-xl font-medium mb-2" data-id="element-3151">Request Not Found</h2>
          <p className="text-gray-600" data-id="element-3152">
            The requested details could not be loaded.
          </p>
        </div>
      </div>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  };
  const isApproved = request.status === 'approved';
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'invoice':
        return <FileTextIcon size={16} className="text-blue-500" data-id="element-3153" />;
      case 'timecard':
        return <ClipboardListIcon size={16} className="text-green-500" data-id="element-3154" />;
      case 'load_tickets':
        return <ClipboardListIcon size={16} className="text-orange-500" data-id="element-3155" />;
      default:
        return <FileIcon size={16} className="text-gray-500" data-id="element-3156" />;
    }
  };
  const handleApprove = () => {
    alert(`Request ${request.id} approved with factor rate of ${customFactorRate}%`);
    setShowApproveConfirm(false);
    onClose();
  };
  const handleDeny = () => {
    alert(`Request ${request.id} denied. Reason: ${decisionNotes}`);
    setShowDenyConfirm(false);
    onClose();
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-3157">
      <div className="p-4 border-b flex items-center justify-between" data-id="element-3158">
        <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3159">
          <ArrowLeftIcon size={18} data-id="element-3160" />
          Back to List
        </button>
        <h2 className="text-lg font-medium text-center flex-1" data-id="element-3161">
          Factoring Request Details
        </h2>
        <div className="w-24" data-id="element-3162"></div> {/* Spacer for alignment */}
      </div>
      <div className="p-6" data-id="element-3163">
        <div className="flex flex-col md:flex-row gap-6 mb-6" data-id="element-3164">
          {/* Left Column - Request Information */}
          <div className="flex-1 space-y-6" data-id="element-3165">
            <div className="flex items-start gap-4" data-id="element-3166">
              <div className="h-12 w-12 rounded-full bg-[#0F2A52] flex items-center justify-center text-white" data-id="element-3167">
                <TruckIcon size={24} data-id="element-3168" />
              </div>
              <div data-id="element-3169">
                <h3 className="text-xl font-medium" data-id="element-3170">
                  {request.truckingCompany}
                </h3>
                <p className="text-gray-600" data-id="element-3171">
                  {request.invoiceNumber} • {formatDate(request.requestDate)}
                </p>
                <div className="mt-2" data-id="element-3172">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${request.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-3173">
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${request.priority === 'high' ? 'bg-red-100 text-red-800' : request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-3174">
                    {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}{' '}
                    Priority
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3175">
              <h4 className="font-medium mb-3 flex items-center gap-2" data-id="element-3176">
                <BuildingIcon size={16} className="text-[#0F2A52]" data-id="element-3177" />
                Job Information
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-3178">
                <div data-id="element-3179">
                  <p className="text-sm text-gray-500" data-id="element-3180">Job Name</p>
                  <p className="font-medium" data-id="element-3181">{request.jobName}</p>
                </div>
                <div data-id="element-3182">
                  <p className="text-sm text-gray-500" data-id="element-3183">Job Number</p>
                  <p className="font-medium" data-id="element-3184">{request.jobNumber}</p>
                </div>
                <div data-id="element-3185">
                  <p className="text-sm text-gray-500" data-id="element-3186">Contractor</p>
                  <p className="font-medium" data-id="element-3187">{request.contractor}</p>
                </div>
                <div data-id="element-3188">
                  <p className="text-sm text-gray-500" data-id="element-3189">Contractor Rating</p>
                  <p className="font-medium" data-id="element-3190">{request.contractorRating}</p>
                </div>
                <div data-id="element-3191">
                  <p className="text-sm text-gray-500" data-id="element-3192">Completion Date</p>
                  <p className="font-medium" data-id="element-3193">
                    {formatDate(request.completionDate)}
                  </p>
                </div>
                <div data-id="element-3194">
                  <p className="text-sm text-gray-500" data-id="element-3195">Payment Terms</p>
                  <p className="font-medium" data-id="element-3196">{request.paymentTerms}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3197">
              <h4 className="font-medium mb-3 flex items-center gap-2" data-id="element-3198">
                <DollarSignIcon size={16} className="text-[#0F2A52]" data-id="element-3199" />
                Financial Details
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-3200">
                <div data-id="element-3201">
                  <p className="text-sm text-gray-500" data-id="element-3202">Invoice Amount</p>
                  <p className="text-xl font-medium text-[#0F2A52]" data-id="element-3203">
                    {formatCurrency(request.amount)}
                  </p>
                </div>
                <div data-id="element-3204">
                  <p className="text-sm text-gray-500" data-id="element-3205">Factor Rate</p>
                  <p className="font-medium" data-id="element-3206">{request.factorRate}%</p>
                </div>
                <div data-id="element-3207">
                  <p className="text-sm text-gray-500" data-id="element-3208">Factoring Fee</p>
                  <p className="font-medium" data-id="element-3209">
                    {formatCurrency(request.amount * request.factorRate / 100)}
                  </p>
                </div>
                <div data-id="element-3210">
                  <p className="text-sm text-gray-500" data-id="element-3211">Net Payment</p>
                  <p className="font-medium" data-id="element-3212">
                    {formatCurrency(request.amount - request.amount * request.factorRate / 100)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3213">
              <h4 className="font-medium mb-3 flex items-center gap-2" data-id="element-3214">
                <ClipboardListIcon size={16} className="text-[#0F2A52]" data-id="element-3215" />
                Job Details
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-3216">
                <div data-id="element-3217">
                  <p className="text-sm text-gray-500" data-id="element-3218">Completed Loads</p>
                  <p className="font-medium" data-id="element-3219">{request.completedLoads}</p>
                </div>
                <div data-id="element-3220">
                  <p className="text-sm text-gray-500" data-id="element-3221">Total Hours</p>
                  <p className="font-medium" data-id="element-3222">{request.totalHours}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3223">
              <h4 className="font-medium mb-3 flex items-center gap-2" data-id="element-3224">
                <FileTextIcon size={16} className="text-[#0F2A52]" data-id="element-3225" />
                Notes
              </h4>
              <p className="text-gray-700 whitespace-pre-line" data-id="element-3226">
                {request.notes}
              </p>
            </div>
            {isApproved && request.approvalDate && <div className="bg-green-50 p-4 rounded-lg border border-green-200" data-id="element-3227">
                <h4 className="font-medium mb-3 flex items-center gap-2 text-green-800" data-id="element-3228">
                  <CheckCircleIcon size={16} className="text-green-600" data-id="element-3229" />
                  Approval Information
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-3230">
                  <div data-id="element-3231">
                    <p className="text-sm text-green-700" data-id="element-3232">Approval Date</p>
                    <p className="font-medium text-green-900" data-id="element-3233">
                      {formatDate(request.approvalDate)}
                    </p>
                  </div>
                  {request.achFileName && <div data-id="element-3234">
                      <p className="text-sm text-green-700" data-id="element-3235">ACH File</p>
                      <p className="font-medium text-green-900" data-id="element-3236">
                        {request.achFileName}
                      </p>
                    </div>}
                  {request.achGenerationDate && <div data-id="element-3237">
                      <p className="text-sm text-green-700" data-id="element-3238">
                        ACH Generation Date
                      </p>
                      <p className="font-medium text-green-900" data-id="element-3239">
                        {formatDate(request.achGenerationDate)}
                      </p>
                    </div>}
                  {request.expectedPaymentDate && <div data-id="element-3240">
                      <p className="text-sm text-green-700" data-id="element-3241">
                        Expected Payment Date
                      </p>
                      <p className="font-medium text-green-900" data-id="element-3242">
                        {formatDate(request.expectedPaymentDate)}
                      </p>
                    </div>}
                </div>
              </div>}
          </div>
          {/* Right Column - Documents and Actions */}
          <div className="md:w-1/3 space-y-6" data-id="element-3243">
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3244">
              <h4 className="font-medium mb-3 flex items-center gap-2" data-id="element-3245">
                <FileIcon size={16} className="text-[#0F2A52]" data-id="element-3246" />
                Supporting Documents
              </h4>
              <ul className="space-y-2" data-id="element-3247">
                {request.documents.map((doc, index) => <li key={index} className="flex items-center justify-between p-2 hover:bg-white rounded" data-id="element-3248">
                    <span className="flex items-center gap-2" data-id="element-3249">
                      {getDocumentIcon(doc.type)}
                      <span className="text-sm" data-id="element-3250">
                        {doc.type.charAt(0).toUpperCase() + doc.type.slice(1).replace('_', ' ')}
                      </span>
                    </span>
                    <button className="text-blue-600 hover:text-blue-800" data-id="element-3251">
                      <DownloadIcon size={16} data-id="element-3252" />
                    </button>
                  </li>)}
              </ul>
            </div>
            {!isApproved && <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3253">
                <h4 className="font-medium mb-4 flex items-center gap-2" data-id="element-3254">
                  <InfoIcon size={16} className="text-[#0F2A52]" data-id="element-3255" />
                  Decision
                </h4>
                <div className="mb-4" data-id="element-3256">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3257">
                    Factor Rate (%)
                  </label>
                  <input type="number" step="0.1" min="0" max="10" value={customFactorRate} onChange={e => setCustomFactorRate(parseFloat(e.target.value))} className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" data-id="element-3258" />
                </div>
                <div className="mb-4" data-id="element-3259">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3260">
                    Notes
                  </label>
                  <textarea rows={3} value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)} placeholder="Add any notes regarding your decision..." className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" data-id="element-3261"></textarea>
                </div>
                <div className="flex gap-3" data-id="element-3262">
                  <button onClick={() => setShowApproveConfirm(true)} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-3263">
                    <CheckCircleIcon size={18} data-id="element-3264" />
                    Approve
                  </button>
                  <button onClick={() => setShowDenyConfirm(true)} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-3265">
                    <XCircleIcon size={18} data-id="element-3266" />
                    Deny
                  </button>
                </div>
              </div>}
            {isApproved && <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-3267">
                <div className="flex items-center justify-center gap-3 text-green-700 mb-3" data-id="element-3268">
                  <CheckCircleIcon size={24} className="text-green-600" data-id="element-3269" />
                  <span className="text-lg font-medium" data-id="element-3270">Approved</span>
                </div>
                <p className="text-center text-green-600 text-sm mb-3" data-id="element-3271">
                  This request has been approved and processed for payment.
                </p>
                <button className="w-full bg-[#0F2A52] hover:bg-[#0F2A52]/90 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2" data-id="element-3272">
                  <FileTextIcon size={18} data-id="element-3273" />
                  View Payment Details
                </button>
              </div>}
          </div>
        </div>
      </div>
      {/* Approve Confirmation Modal */}
      {showApproveConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-3274">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full" data-id="element-3275">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2" data-id="element-3276">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-3277" />
              Confirm Approval
            </h3>
            <p className="mb-4 text-gray-700" data-id="element-3278">
              You are about to approve factoring request{' '}
              <strong data-id="element-3279">{request.invoiceNumber}</strong> for{' '}
              <strong data-id="element-3280">{formatCurrency(request.amount)}</strong> with a factor
              rate of <strong data-id="element-3281">{customFactorRate}%</strong>.
            </p>
            <p className="mb-4 text-gray-700" data-id="element-3282">
              This will authorize a payment of{' '}
              <strong data-id="element-3283">
                {formatCurrency(request.amount - request.amount * customFactorRate / 100)}
              </strong>{' '}
              to {request.truckingCompany}.
            </p>
            <div className="flex justify-end gap-3" data-id="element-3284">
              <button onClick={() => setShowApproveConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-3285">
                Cancel
              </button>
              <button onClick={handleApprove} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md" data-id="element-3286">
                Confirm Approval
              </button>
            </div>
          </div>
        </div>}
      {/* Deny Confirmation Modal */}
      {showDenyConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-3287">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full" data-id="element-3288">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2" data-id="element-3289">
              <XCircleIcon size={20} className="text-red-600" data-id="element-3290" />
              Confirm Denial
            </h3>
            <p className="mb-4 text-gray-700" data-id="element-3291">
              You are about to deny factoring request{' '}
              <strong data-id="element-3292">{request.invoiceNumber}</strong> from{' '}
              <strong data-id="element-3293">{request.truckingCompany}</strong>.
            </p>
            {decisionNotes ? <div className="mb-4 p-3 bg-gray-50 rounded-md" data-id="element-3294">
                <p className="text-sm font-medium text-gray-700" data-id="element-3295">
                  Reason for denial:
                </p>
                <p className="text-gray-700" data-id="element-3296">{decisionNotes}</p>
              </div> : <div className="mb-4 p-3 bg-yellow-50 rounded-md text-yellow-800 flex items-start gap-2" data-id="element-3297">
                <AlertTriangleIcon size={16} className="mt-0.5 flex-shrink-0" data-id="element-3298" />
                <p className="text-sm" data-id="element-3299">
                  No reason for denial provided. It's recommended to provide
                  feedback to the trucking company.
                </p>
              </div>}
            <div className="flex justify-end gap-3" data-id="element-3300">
              <button onClick={() => setShowDenyConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-3301">
                Cancel
              </button>
              <button onClick={handleDeny} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md" data-id="element-3302">
                Confirm Denial
              </button>
            </div>
          </div>
        </div>}
    </div>;
};